import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface BankClosingSummaryDTO {
    bankId: number;
    month: number;
    year: number;
    pendingReconciliationsCount: number;
    unreconciledIncomeCount: number;
    unreconciledExpenseCount: number;
    totalCredits: number;
    totalDebits: number;
    projectedStarBalance: number;
    projectedEndBalance: number;
    canClose: boolean;
    warningMessage?: string;
}

export interface PeriodVoucherDTO {
    voucherType: string; // "INCOME" or "EXPENSE"
    voucherId: number;
    documentNumber: string;
    createdAt: Date;
    paymentDate: Date;
    amount: number;
    isReconciled: boolean;
    reconciliationType?: string; // "MANUAL", "AUTOMATIC", or null
    bankReconciliationId?: number;
    reconciliationName?: string;
}

export interface PeriodDetailsResponseDTO {
    bankId: number;
    month: number;
    year: number;
    vouchers: PeriodVoucherDTO[];
    totalVouchers: number;
    reconciledCount: number;
    unreconciledCount: number;
}

// Assuming 'Result' is a globally available type or imported from another file not provided.
type Result<T> = {
    isSuccess: boolean;
    data?: T;
    errorMessage?: string;
};

@Injectable({
    providedIn: 'root'
})
export class BankClosingService {
    private apiUrl = `${environment.apiUrl}/BankClosing`;

    constructor(private http: HttpClient) { }

    /**
     * Cierra un periodo bancario
     */
    closePeriod(request: BankClosingRequestDTO): Observable<Result<BankClosingResponseDTO>> {
        return this.http.post<Result<BankClosingResponseDTO>>(`${this.apiUrl}`, request);
    }

    /**
     * Verifica si un periodo está cerrado
     */
    checkStatus(bankId: number, year: number, month: number): Observable<Result<boolean>> {
        return this.http.get<Result<boolean>>(`${this.apiUrl}/status/${bankId}/${year}/${month}`);
    }

    /**
     * Obtiene el resumen de un cierre completado
     */
    getClosureSummary(bankId: number, year: number, month: number): Observable<Result<BankClosingResponseDTO>> {
        return this.http.get<Result<BankClosingResponseDTO>>(`${this.apiUrl}/summary/${bankId}/${year}/${month}`);
    }

    getPreClosingSummary(bankId: number, month: number, year: number): Observable<Result<BankClosingSummaryDTO>> {
        const params = new HttpParams()
            .set('bankId', bankId.toString())
            .set('month', month.toString())
            .set('year', year.toString());
        return this.http.get<Result<BankClosingSummaryDTO>>(`${this.apiUrl}/pre-summary`, { params });
    }

    /**
     * Obtiene todos los comprobantes de un período con detalles de conciliación
     */
    getPeriodDetails(bankId: number, month: number, year: number): Observable<Result<PeriodDetailsResponseDTO>> {
        const params = new HttpParams()
            .set('bankId', bankId.toString())
            .set('month', month.toString())
            .set('year', year.toString());
        return this.http.get<Result<PeriodDetailsResponseDTO>>(`${this.apiUrl}/period-details`, { params });
    }

    /**
     * Obtiene todos los períodos cerrados con filtros opcionales
     */
    getClosedPeriods(bankId?: number, year?: number): Observable<Result<BankClosingResponseDTO[]>> {
        let params = new HttpParams();
        if (bankId) params = params.set('bankId', bankId.toString());
        if (year) params = params.set('year', year.toString());
        return this.http.get<Result<BankClosingResponseDTO[]>>(`${this.apiUrl}/closed-periods`, { params });
    }
}

export interface BankClosingRequestDTO {
    bankId: number;
    month: number;
    year: number;
    totalItemsND?: number;
    totalItemsNC?: number;
}

export interface BankClosingResponseDTO {
    id: number;
    bankId: number;
    month: number;
    year: number;
    startBalance: number;
    endBalance: number;
    totalCredits: number;
    totalDebits: number;
    totalReconciledAmount: number;
    totalDiscounts: number;
    closureSummary: string;
    notes?: string;
    closedAt: string;
    closedBy: string;
    bank: {
        id: number;
        bankName: string;
        accountNumber: string;
    };
}
